import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Droplet, Plus, TrendingUp, TrendingDown, Trash2, RefreshCw } from 'lucide-react';
import { glucoseAPI } from '../services/api';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const GlucoseLog = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newLog, setNewLog] = useState({
    glucose_value: '',
    meal_relation: 'Before Breakfast',
    notes: ''
  });

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const data = await glucoseAPI.getAll(100);
      setLogs(data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch glucose logs",
        variant: "destructive"
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const handleAddLog = async () => {
    if (!newLog.glucose_value) {
      toast({
        title: "Error",
        description: "Please enter a glucose value",
        variant: "destructive"
      });
      return;
    }

    setSubmitting(true);
    try {
      const logData = {
        glucose_value: parseInt(newLog.glucose_value),
        meal_relation: newLog.meal_relation,
        notes: newLog.notes || null
      };

      const createdLog = await glucoseAPI.create(logData);
      setLogs([createdLog, ...logs]);
      setDialogOpen(false);
      setNewLog({
        glucose_value: '',
        meal_relation: 'Before Breakfast',
        notes: ''
      });

      toast({
        title: "Success!",
        description: "Glucose reading added successfully"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to add glucose reading",
        variant: "destructive"
      });
    }
    setSubmitting(false);
  };

  const handleDeleteLog = async (logId) => {
    try {
      await glucoseAPI.delete(logId);
      setLogs(logs.filter(log => log.id !== logId));
      toast({
        title: "Deleted",
        description: "Glucose reading removed"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete reading",
        variant: "destructive"
      });
    }
  };

  const targetMin = user?.target_glucose_min || 80;
  const targetMax = user?.target_glucose_max || 130;

  const avgGlucose = logs.length > 0 
    ? Math.round(logs.reduce((sum, log) => sum + log.glucose_value, 0) / logs.length)
    : 0;

  const inRangeCount = logs.filter(log => log.glucose_value >= targetMin && log.glucose_value <= targetMax).length;
  const inRangePercent = logs.length > 0 ? Math.round((inRangeCount / logs.length) * 100) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Blood Sugar Log</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Track and monitor your glucose levels</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchLogs} className="dark:border-gray-600 dark:text-gray-300">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-emerald-500 to-blue-500 hover:from-emerald-600 hover:to-blue-600 shadow-lg hover:shadow-xl transition-all">
                <Plus className="w-4 h-4 mr-2" />
                Add Reading
              </Button>
            </DialogTrigger>
            <DialogContent className="dark:bg-gray-800">
              <DialogHeader>
                <DialogTitle className="dark:text-white">Add Glucose Reading</DialogTitle>
                <DialogDescription className="dark:text-gray-400">
                  Log your current blood sugar level
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label htmlFor="glucose" className="dark:text-gray-300">Glucose Level (mg/dL)</Label>
                  <Input
                    id="glucose"
                    type="number"
                    placeholder="Enter value"
                    value={newLog.glucose_value}
                    onChange={(e) => setNewLog({ ...newLog, glucose_value: e.target.value })}
                    className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="meal-relation" className="dark:text-gray-300">Meal Relation</Label>
                  <Select
                    value={newLog.meal_relation}
                    onValueChange={(value) => setNewLog({ ...newLog, meal_relation: value })}
                  >
                    <SelectTrigger className="dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-gray-700">
                      <SelectItem value="Before Breakfast">Before Breakfast</SelectItem>
                      <SelectItem value="After Breakfast">After Breakfast</SelectItem>
                      <SelectItem value="Before Lunch">Before Lunch</SelectItem>
                      <SelectItem value="After Lunch">After Lunch</SelectItem>
                      <SelectItem value="Before Dinner">Before Dinner</SelectItem>
                      <SelectItem value="After Dinner">After Dinner</SelectItem>
                      <SelectItem value="Bedtime">Bedtime</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes" className="dark:text-gray-300">Notes (optional)</Label>
                  <Input
                    id="notes"
                    type="text"
                    placeholder="Any additional notes"
                    value={newLog.notes}
                    onChange={(e) => setNewLog({ ...newLog, notes: e.target.value })}
                    className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <Button 
                  onClick={handleAddLog} 
                  className="w-full bg-gradient-to-r from-emerald-500 to-blue-500"
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : 'Save Reading'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-3 gap-6">
        <Card className="shadow-md border-0 dark:bg-gray-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Average Glucose</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">{avgGlucose || '--'}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">mg/dL</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
                <Droplet className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-md border-0 dark:bg-gray-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">In Range</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">{inRangePercent}%</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{targetMin}-{targetMax} mg/dL</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                {inRangePercent >= 70 ? (
                  <TrendingUp className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                ) : (
                  <TrendingDown className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-md border-0 dark:bg-gray-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Readings</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">{logs.length}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">logged</p>
              </div>
              <div className="w-12 h-12 bg-violet-100 dark:bg-violet-900/30 rounded-full flex items-center justify-center">
                <Droplet className="w-6 h-6 text-violet-600 dark:text-violet-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Logs List */}
      <Card className="shadow-md border-0 dark:bg-gray-800">
        <CardHeader>
          <CardTitle className="dark:text-white">Recent Readings</CardTitle>
          <CardDescription className="dark:text-gray-400">Your glucose log history</CardDescription>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <Droplet className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg">No glucose readings yet</p>
              <p className="text-sm mt-1">Click "Add Reading" to log your first entry</p>
            </div>
          ) : (
            <div className="space-y-3">
              {logs.map((log) => {
                const isInRange = log.glucose_value >= targetMin && log.glucose_value <= targetMax;
                const isHigh = log.glucose_value > targetMax;
                const isLow = log.glucose_value < targetMin;
                
                return (
                  <div 
                    key={log.id} 
                    className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                      isInRange 
                        ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800' 
                        : isHigh 
                          ? 'bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-800'
                          : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
                    }`}
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        isInRange 
                          ? 'bg-emerald-100 dark:bg-emerald-900/50' 
                          : isHigh 
                            ? 'bg-orange-100 dark:bg-orange-900/50'
                            : 'bg-red-100 dark:bg-red-900/50'
                      }`}>
                        <span className={`text-lg font-bold ${
                          isInRange 
                            ? 'text-emerald-600 dark:text-emerald-400' 
                            : isHigh 
                              ? 'text-orange-600 dark:text-orange-400'
                              : 'text-red-600 dark:text-red-400'
                        }`}>
                          {log.glucose_value}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{log.glucose_value} mg/dL</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{log.meal_relation}</p>
                        {log.notes && <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{log.notes}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {new Date(log.time).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {new Date(log.time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                        </p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => handleDeleteLog(log.id)}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GlucoseLog;
